import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import _ from "lodash";
const uuidv4 = () => crypto.randomUUID();
import usePrevious from "src/util/usePrevious";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useAISidebar } from "src/hooks/useAISidebar";
import { EventBus, EVENTS } from "src/services/eventEmitter";
import {
  useApolloSettings,
  useTenantId,
  useDateSettings,
} from "src/contexts/global/ApolloSettingsContext";
import {
  useWorkspaceFeatureFlags,
  useWorkspace,
} from "src/contexts/global/WorkspaceContext";

import { API_DATE_FORMAT } from "src/consts/global";
import { isInternalSupportUser } from "src/services/auth";

import {
  AGENTCORE_NAMES,
  ASK_TEAMFORM_MODES,
  SOURCE_TYPES,
  isAskTeamformV2Agent,
} from "../consts";
import streamAIResponse from "../utils/streamAIResponse";
import { AIChatContext } from "./AIChatContext";
import useGetAIChat from "./hooks/useGetAIChat";

const markFailedPreviousAttempts = (messages) => {
  const userMessageIndex = _.findLastIndex(messages, (m) => {
    return m.role === "user";
  });
  return messages
    .map((m, i) => ({
      ...m,
      failed: m.failed || userMessageIndex < i,
    }))
    .filter((m) => m.failed)
    .map((m) => m.id);
};

export const AIChatProvider = ({
  children,
  sourceId,
  selectedAgent: selectedAgentProp,
  setSelectedAgent: setSelectedAgentProp,
}) => {
  const authState = useAuth0();
  const isSupportUser = isInternalSupportUser(authState?.user);
  const {
    enableAskTeamformV2,
    enableAIChat,
    enableSupportQuestionInAskTeamform,
  } = useWorkspaceFeatureFlags();
  const [askTeamformMode, setAskTeamformMode] = useState(
    ASK_TEAMFORM_MODES.DATA
  );
  const [selectedAgentInternal, setSelectedAgentInternal] = useState(
    enableAskTeamformV2
      ? AGENTCORE_NAMES.ASK_TEAMFORM_V2
      : AGENTCORE_NAMES.ASK_TEAMFORM
  );
  const selectedAgent = selectedAgentProp ?? selectedAgentInternal;
  const setSelectedAgent = setSelectedAgentProp ?? setSelectedAgentInternal;
  const workspace = useWorkspace();
  const { id: workspaceId } = workspace;
  const [question, setQuestion] = useState("");

  const [complete, setComplete] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toolsUsedCount, setToolsUsedCount] = useState(0);
  const [error, setError] = useState(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const { apiUrl } = useApolloSettings();
  const tenantId = useTenantId();
  const dateSettings = useDateSettings();
  const pitDate = dateSettings.toDate?.format(API_DATE_FORMAT);

  const {
    chatId,
    chats,
    loading: chatsLoading,
    onStartNewChat: rawOnStartNewChat,
    onSelectChat: rawOnSelectChat,
    adoptChatId,
    currentChat,
    updateChatOptimistically,
    onSubmitMessageFeedback,
    refetchChats,
  } = useGetAIChat();

  // History/thread switches must not keep Help mode from a prior chat.
  const onStartNewChat = useCallback(
    (...args) => {
      setAskTeamformMode(ASK_TEAMFORM_MODES.DATA);
      return rawOnStartNewChat(...args);
    },
    [rawOnStartNewChat]
  );

  const onSelectChat = useCallback(
    (...args) => {
      setAskTeamformMode(ASK_TEAMFORM_MODES.DATA);
      return rawOnSelectChat(...args);
    },
    [rawOnSelectChat]
  );

  const [filteredMessageIds, setFilteredMessageIds] = useState([]);

  const rawMessages = currentChat?.messages;

  /** @type {{ versionId: string|null, updateSidebarState: Function }} AI sidebar state and controls */
  const { versionId } = useAISidebar();
  /**
   * Determines if AI chat is enabled based on feature flags and source
   * @type {boolean}
   * @memoized
   */
  const aiEnabled = useMemo(
    () => enableAIChat && sourceId === SOURCE_TYPES.chat_q_and_a,
    [enableAIChat, sourceId]
  );

  useEffect(() => {
    if (error) {
      setFilteredMessageIds(markFailedPreviousAttempts(rawMessages || []));
    }
  }, [error, rawMessages]);

  const messages = useMemo(() => {
    let ms = [];
    if (rawMessages) {
      ms = rawMessages.filter((m) => !filteredMessageIds.includes(m.id));
    }

    return ms;
  }, [filteredMessageIds, rawMessages]);

  const previousMessages = usePrevious(messages);

  /**
   * Helper function to clear messages and reset state
   * @returns {void}
   */
  const onSessionClear = useCallback(() => {
    // Reset current question and state
    setUserHasScrolledUp(false);
    setError(null);
    setQuestion("");
    setComplete(true);
    setEditingMessageId(null);
    setAskTeamformMode(ASK_TEAMFORM_MODES.DATA);
  }, [setError]);

  /** @type {React.MutableRefObject<string|null>} Previous version ID for change detection */
  const previousVersionId = useRef(versionId);

  /**
   * Effect for managing chat session lifecycle based on versionId changes
   * @effect
   * @description Clears session when versionId changes from value to null,
   * sets loading state for new sessions, and tracks versionId changes
   * @param {Function} clearChatSession - Session clearing function
   * @param {string|null} versionId - Current version identifier
   * @param {number} messages.length - Number of current messages
   * @param {Function} setComplete - Completion state setter
   */
  useEffect(() => {
    // Clear session only when versionId changes from a value to null
    if (previousVersionId.current && !versionId) {
      onSessionClear();
    }

    previousVersionId.current = versionId;
  }, [onSessionClear, versionId, messages.length, setComplete]);

  const invokeAIResponse = useCallback(
    async (newQuestion, { isRetry = false } = {}) => {
      try {
        setQuestion("");
        setEditingMessageId(null);
        setIsSubmitting(true);
        setComplete(false);
        setToolsUsedCount(0);

        // Optimistically show the user message immediately (skip on retry since it already exists)
        if (!isRetry) {
          updateChatOptimistically({
            id: `temp-user-${uuidv4()}`,
            role: "user",
            content: newQuestion,
          });
        }

        const token = await authState.getTokenSilently();

        // Track the chatId returned by the backend (may be new if no chatId was sent)
        let responseChatId = null;

        const sendAskTeamformMode =
          enableSupportQuestionInAskTeamform &&
          isAskTeamformV2Agent(selectedAgent);

        streamAIResponse(
          {
            prompt: newQuestion,
            aiChatId: chatId,
            ...(sendAskTeamformMode && {
              askTeamformMode:
                askTeamformMode === ASK_TEAMFORM_MODES.SUPPORT
                  ? ASK_TEAMFORM_MODES.SUPPORT
                  : ASK_TEAMFORM_MODES.DATA,
            }),
          },
          {
            tenantId,
            apiUrl,
            agentName: selectedAgent,
            headers: {
              Authorization: `Bearer ${token}`,
              "x-workspace-id": workspaceId,
              "x-pit-date": pitDate,
            },

            onError: (e) => {
              setError(e);
              setComplete(true);
              setIsSubmitting(false);
            },

            onMessageChunk: (chunkEvent) => {
              if (chunkEvent?.message) {
                if (!responseChatId && chunkEvent.message.aiChatId) {
                  responseChatId = chunkEvent.message.aiChatId;
                }

                // Extract <tools-used value="N"> and update tool count
                const rawContent =
                  chunkEvent.content || chunkEvent.message.content;
                const toolsMatch = rawContent?.match(
                  /<tools-used\s+value="(\d+)">/
                );
                if (toolsMatch) {
                  setToolsUsedCount(parseInt(toolsMatch[1], 10));
                }
                // Strip the tag before displaying
                const displayContent = rawContent
                  ?.replace(/<tools-used\s+value="\d+">/g, "")
                  ?.trim();

                // Skip optimistic update for tool-count-only chunks
                if (!displayContent && toolsMatch) {
                  return;
                }

                const messageWithFormattedContent = {
                  ...chunkEvent.message,
                  content: displayContent,
                };
                updateChatOptimistically(messageWithFormattedContent);
              }
            },
            onDone: () => {
              // If the backend created a new chat, adopt its ID and refresh the chat list
              if (responseChatId && responseChatId !== chatId) {
                adoptChatId(responseChatId);
                refetchChats();
              }

              setComplete(true);
              setIsSubmitting(false);
              setToolsUsedCount(0);
            },
            onMessage: (messageEvent) => {
              if (messageEvent?.final_message) {
                // Strip <tools-used> tags from final message
                const cleaned = {
                  ...messageEvent.final_message,
                  content: messageEvent.final_message.content?.replace(
                    /<tools-used\s+value="\d+">/g,
                    ""
                  ),
                };
                updateChatOptimistically(cleaned);
              }

              if (
                messageEvent?.final_message?.content?.includes("<dashboard")
              ) {
                const dashboardId = messageEvent?.final_message?.content?.match(
                  /<dashboard[^>]*\bid="([^"]+)"/
                )?.[1];
                const title = messageEvent?.final_message?.content?.match(
                  /<dashboard[^>]*\btitle="([^"]+)"/
                )?.[1];

                if (dashboardId || title) {
                  EventBus.emit(EVENTS.AI_DASHBOARD_UPDATED, {
                    dashboardId,
                    title,
                  });
                }
              }
            },
          }
        );
      } catch (e) {
        setError(e);
        setIsSubmitting(false);
        setComplete(true);
      }
    },
    [
      authState,
      chatId,
      tenantId,
      apiUrl,
      workspaceId,
      updateChatOptimistically,
      adoptChatId,
      refetchChats,
      pitDate,
      selectedAgent,
      enableSupportQuestionInAskTeamform,
      askTeamformMode,
    ]
  );

  const onSendQuestion = useCallback(async () => {
    if (editingMessageId) {
      const messageToUpdate = messages.find((m) => m.id === editingMessageId);
      if (!messageToUpdate) {
        return;
      }
      setIsSubmitting(true);
      setComplete(false);
      setToolsUsedCount(0);
      setError(null);
      setEditingMessageId(null);
      setQuestion("");

      // Update the local message content optimistically (backend persists it)
      updateChatOptimistically({
        ...messageToUpdate,
        content: question,
      });

      invokeAIResponse(question, { isRetry: true });
    } else {
      invokeAIResponse(question);
    }
  }, [
    editingMessageId,
    question,
    messages,
    updateChatOptimistically,
    invokeAIResponse,
  ]);

  const lastUserMessage = useMemo(() => {
    const userMessage = _.findLast(messages, (m) => {
      return m.role === "user";
    });

    const lastMessage = _.last(messages);

    if (
      lastMessage?.role === "assistant" &&
      lastMessage.content?.length === 0
    ) {
      return userMessage;
    }

    if (lastMessage?.role === "user") {
      return lastMessage;
    }

    return null;
  }, [messages]);

  const canRetryOrEdit = useMemo(
    () => (!!error && !isSubmitting) || (!!lastUserMessage && !isSubmitting),
    [error, isSubmitting, lastUserMessage]
  );

  const onRetry = useCallback(() => {
    if (!lastUserMessage?.content) {
      return;
    }
    setIsSubmitting(true);
    setComplete(false);
    setToolsUsedCount(0);
    setError(null);
    invokeAIResponse(lastUserMessage.content, { isRetry: true });
  }, [invokeAIResponse, lastUserMessage]);

  const onEditLastMessage = useCallback(() => {
    if (!lastUserMessage) {
      return;
    }
    setQuestion(lastUserMessage.content || "");
    setEditingMessageId(lastUserMessage.id);
    setError(null);
  }, [lastUserMessage]);

  const onCancelEdit = useCallback(() => {
    setEditingMessageId(null);
    setQuestion("");
  }, []);

  const showLoader = useMemo(() => {
    return isSubmitting;
  }, [isSubmitting]);

  const values = useMemo(
    () => ({
      canRetryOrEdit,
      chatId,
      editingMessageId,
      messages,
      previousMessages,
      complete,
      hasError: !!error,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      onEditLastMessage,
      onCancelEdit,
      error,
      setError,
      onStartNewChat,
      chats,
      chatsLoading,
      onSelectChat,
      toolsUsedCount,
      onSubmitMessageFeedback,
      selectedAgent: isSupportUser
        ? selectedAgent
        : AGENTCORE_NAMES.ASK_TEAMFORM,
      setSelectedAgent: isSupportUser ? setSelectedAgent : () => {},
      isSupportUser,
      askTeamformMode,
      setAskTeamformMode,
      enableSupportQuestionInAskTeamform,
    }),
    [
      canRetryOrEdit,
      chatId,
      editingMessageId,
      messages,
      previousMessages,
      complete,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      onEditLastMessage,
      onCancelEdit,
      error,
      setError,
      onStartNewChat,
      chats,
      chatsLoading,
      onSelectChat,
      toolsUsedCount,
      onSubmitMessageFeedback,
      selectedAgent,
      setSelectedAgent,
      isSupportUser,
      askTeamformMode,
      enableSupportQuestionInAskTeamform,
    ]
  );

  if (!aiEnabled) {
    return null;
  }

  return (
    <AIChatContext.Provider value={values}>{children}</AIChatContext.Provider>
  );
};

AIChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  sourceId: PropTypes.string.isRequired,
  selectedAgent: PropTypes.string,
  setSelectedAgent: PropTypes.func,
};
